import {Switch} from '@wandb/weave/components';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useCallback, useMemo, useRef} from 'react';
import {Tab} from 'semantic-ui-react';
import {twMerge} from 'tailwind-merge';

import {SmoothingType, XAxisFormatValues} from '../../../generated/graphql';
import {type SettingType} from '../../../services/analytics/workspaceSettingsEvents';
import type * as QueryTypes from '../../../state/runs/types';
import {type Line, prettyXAxisLabel} from '../../../util/plotHelpers';
import {
  useFullScreenLinePlotMaxRunsDynamicConfig,
  useLinePlotMaxRunsDynamicConfig,
} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {
  useRampFlagFullFidelityRunsPerChart,
  useRampFlagIncreaseFullScreenMaxRuns,
} from '../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {useIsNgwEnabled} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import RangeInput from '../../elements/RangeInput';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {useIsFullScreenPanel} from '../../Panel/hooks/useIsFullScreenPanel';
import {usePanelActionsContext} from '../../Panel/PanelEditor/PanelEditor';
import {DebouncedInputOption} from '../../PanelChartOptions';
import {useMaybeWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {SettingIndentedRow} from '../../WorkspaceDrawer/Settings/components/SettingIndentedRow';
import {SettingLabel} from '../../WorkspaceDrawer/Settings/components/SettingLabel';
import {useTrackSettingsChange} from '../../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {LinePlotConfigExcludeOutliers} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotConfigExcludeOutliers';
import {LinePlotPointAggregation} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotPointAggregation';
import {
  EMPTY_SMOOTHING_SETTINGS,
  LinePlotSmoothing,
} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotSmoothing';
import {getMaxRunsCeiling} from '../../WorkspaceDrawer/Settings/runLinePlots/utils/getCascadingMaxRuns';
import {
  XAxisFormat,
  xAxisIsBuiltin,
} from '../../WorkspaceDrawer/Settings/runLinePlots/XAxisFormat';
import {
  type DerivedPointVisualizationOption,
  FULL_FIDELITY_MODES,
  type FullFidelityMode,
  isPointVizFullFidelity,
} from '../../WorkspaceDrawer/Settings/types';
import {defaultTitle} from '../common';
import {PlotTypeSelection} from '../edit/PlotTypeSelection';
import {
  getConfigZoomUsageType,
  useLinePlotZoomUsageTracker,
} from '../observability/zoomUsage';
import {usePanelConfigContext} from '../PanelConfigContext';
import {usePanelInteractionContext} from '../PanelInteractionContext';
import {usePanelZoom} from '../PanelZoomContext';
import {usePanelGroupingSettings} from '../RunsLinePlotContext/usePanelGroupingSettings';
import {type RunsLinePlotConfig} from '../types';
import {isBarPlot, isEmptyChart} from '../utils/checkTypes';
import {usePanelZoom2} from '../zoom/PanelZoomContext2';
import {EditMaxRuns} from './EditMaxRuns';
import {EditRunsTableGrouping} from './EditRunsTableGrouping';
import {HistoryKeysAxisSections} from './HistoryKeysAxisSections';
import {MetricSearchAxisSections} from './MetricSearchAxisSections';

type EditDataTabProps = {
  updateConfig: (config: RunsLinePlotConfig) => void;
  config: RunsLinePlotConfig;
  query: QueryTypes.QueryConfig;
  lines: Line[];
};

const NOOP_TRACK_SETTING = () => {};

export const EditDataTab = ({
  updateConfig,
  config,
  query,
  lines,
}: EditDataTabProps) => {
  const {
    excludeOutliers,
    fullFidelityMode,
    pointVisualizationMethod,
    singlePointRenderMode,
    smoothingType,
    smoothingWeight,
    xAxis,
    xAxisMax,
    xAxisMin,
    xLogScale,
    xAxisFormat,
    yLogScale,
    showOriginalAfterSmoothing,
  } = usePanelConfigContext();
  const {isSingleRun} = useSingleRunModeContext();
  const isBucketing = isPointVizFullFidelity(pointVisualizationMethod);
  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
  const canIncreaseFullScreenMaxRuns = useRampFlagIncreaseFullScreenMaxRuns();
  const dynamicConfigMaxRuns = useLinePlotMaxRunsDynamicConfig();
  const dynamicConfigFullScreenMaxRuns =
    useFullScreenLinePlotMaxRunsDynamicConfig();
  const {isFullScreen} = useIsFullScreenPanel();
  const {handleConfigZoomChange, handleUserZoomChange} = usePanelZoom();
  const {zooming: zooming1} = usePanelInteractionContext();
  const {handleUserZoom, configZoom, zooming: zooming2} = usePanelZoom2();

  const trackPanelSettingsChange = useTrackSettingsChange();
  const {setRunOnApply} = usePanelActionsContext();

  /**
   * Conditional fns based on what kind of plot we're in. We're moving bucketing on to the new zoom context and we plan on deprecating the old one, but for now we're going to have to support both zoom contexts
   * - Bucketing: update the configZoom object directly
   * - Non-bucketing: use the handler from the context
   */
  const updateUserZoom = isBucketing ? handleUserZoomChange : handleUserZoom;
  const updateConfigZoom = isBucketing ? updateConfig : handleConfigZoomChange;
  const zooming = isBucketing ? zooming2 : zooming1;
  const renderingBarPlot = isBarPlot(lines, zooming, singlePointRenderMode);
  const trackZoomUsage = useLinePlotZoomUsageTracker({
    lines,
    metrics: config.metrics,
    xAxis,
  });
  const trackConfigZoomUsage = useCallback(
    (axis: 'x' | 'y', min?: number | null, max?: number | null) => {
      trackZoomUsage({
        type: getConfigZoomUsageType({axis, min, max}),
        source: 'config',
      });
    },
    [trackZoomUsage]
  );

  const settingsToTrack = useRef<Record<string, any>>({});

  const isNgwEnabled = useIsNgwEnabled();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  // The metric-search pickers need workspace run-set refs to query, which only
  // exist in the multi-run workspace (not reports/embeds). So this null check
  // both makes `workspaceRefs` non-null for the pickers and gates the NGW
  // pickers to the workspace surface.
  const useMetricSearchPicker = isNgwEnabled && workspaceRefs != null;

  const metricsPickerConfig = useMemo(
    () => ({
      metricRegex: config.metricRegex,
      metrics: config.metrics,
      useMetricRegex: config.useMetricRegex,
    }),
    [config.metricRegex, config.metrics, config.useMetricRegex]
  );

  const {isAnyRunsetGrouped: isGrouped} = usePanelGroupingSettings();

  const handleXAxisTitleUpdate = useCallback(
    (value: string) => updateConfig({xAxisTitle: value}),
    [updateConfig]
  );

  const handleYAxisTitleUpdate = useCallback(
    (value: string) => updateConfig({yAxisTitle: value}),
    [updateConfig]
  );

  const handleChartTitleUpdate = useCallback(
    (value: string) => updateConfig({chartTitle: value}),
    [updateConfig]
  );

  const handleXAxisChange = useCallback(
    (_e: unknown, {value}: {value?: unknown}) => {
      if (typeof value !== 'string') {
        return;
      }

      const newConfigZoom: Partial<RunsLinePlotConfig> = {
        xAxisMin: undefined,
        xAxisMax: undefined,
      };
      const xAxisFormat =
        value !== config.xAxis && value === '_timestamp'
          ? XAxisFormatValues.Datetime
          : XAxisFormatValues.Number;
      if (!isBucketing) {
        updateConfig({
          xAxis: value,
          xAxisFormat,
        });
      } else {
        newConfigZoom.xAxis = value;
        newConfigZoom.xAxisFormat = xAxisFormat;
      }
      updateConfigZoom(newConfigZoom);
      updateUserZoom({
        xAxisMin: undefined,
        xAxisMax: undefined,
      });
    },
    [config.xAxis, isBucketing, updateConfig, updateConfigZoom, updateUserZoom]
  );

  const handleResetXAxis = useCallback(() => {
    updateConfig({
      xAxis: undefined,
      xAxisMin: undefined,
      xAxisMax: undefined,
      xLogScale: undefined,
      xAxisTitle: undefined,
      xAxisFormat: undefined,
    });
  }, [updateConfig]);

  const handleExcludeOutliersChange = useCallback(
    (areOutliersExcluded: boolean) => {
      settingsToTrack.current = {
        ...settingsToTrack.current,
        excludeOutliers: areOutliersExcluded
          ? 'exclude-outliers'
          : 'include-outliers',
      };

      setRunOnApply(() => () => {
        trackPanelSettingsChange({
          setting: 'excludeOutliers',
          settingValue: settingsToTrack.current?.excludeOutliers,
        });
      });
      updateConfig({
        excludeOutliers: areOutliersExcluded
          ? 'exclude-outliers'
          : 'include-outliers',
        // @ts-expect-error - ignoreOutliers is deprecated
        ignoreOutliers: undefined,
        showMinMaxOnHover: undefined,
      });
    },
    [setRunOnApply, trackPanelSettingsChange, updateConfig]
  );

  const handleClearPointVisualization = useCallback(() => {
    updateConfig({
      pointVisualizationMethod: undefined,
    });
  }, [updateConfig]);

  // Grouped panels opt in through their own key; see useFullFidelityModeLineType.
  const effectiveFullFidelityMode = isGrouped
    ? config.groupedFullFidelityMode
    : fullFidelityMode;
  const ownFullFidelityMode = isGrouped
    ? config.groupedFullFidelityMode
    : config.fullFidelityMode;

  const pointVisualizationDisplayValues = useMemo(
    () => ({
      pointVisualizationMethod,
      fullFidelityMode: effectiveFullFidelityMode,
    }),
    [pointVisualizationMethod, effectiveFullFidelityMode]
  );

  const pointVisualizationValues = useMemo(
    () => ({
      pointVisualizationMethod: config.pointVisualizationMethod,
      fullFidelityMode: ownFullFidelityMode,
    }),
    [config.pointVisualizationMethod, ownFullFidelityMode]
  );

  const handleFullFidelityModeChange = useCallback(
    (value: FullFidelityMode) => {
      settingsToTrack.current = {
        ...settingsToTrack.current,
        fullFidelityMode: value,
      };
      setRunOnApply(() => () => {
        trackPanelSettingsChange({
          setting: 'fullFidelityMode',
          settingValue: settingsToTrack.current?.fullFidelityMode,
        });
      });
      updateConfig(
        isGrouped ? {groupedFullFidelityMode: value} : {fullFidelityMode: value}
      );
    },
    [isGrouped, setRunOnApply, trackPanelSettingsChange, updateConfig]
  );

  const handlePointVisualizationMethodChange = useCallback(
    (value: DerivedPointVisualizationOption) => {
      settingsToTrack.current = {
        ...settingsToTrack.current,
        pointVisualizationMethod: value,
      };
      setRunOnApply(() => () => {
        trackPanelSettingsChange({
          setting: 'pointVisualizationMethod',
          settingValue:
            settingsToTrack.current?.pointVisualizationMethod?.toString(),
        });
      });

      const newRunsLimit = getMaxRunsCeiling({
        canOverrideMaxRuns,
        isBucketing: isPointVizFullFidelity(value),
        isFullScreen,
        canIncreaseFullScreenMaxRuns,
        dynamicConfigMaxRuns,
        dynamicConfigFullScreenMaxRuns,
      });

      updateConfig({
        pointVisualizationMethod: value,
        limit:
          config.limit != null && newRunsLimit
            ? Math.min(config.limit, newRunsLimit)
            : undefined, // don't need to set the limit if it wasn't already set
      });
    },
    [
      canIncreaseFullScreenMaxRuns,
      canOverrideMaxRuns,
      config.limit,
      dynamicConfigFullScreenMaxRuns,
      dynamicConfigMaxRuns,
      isFullScreen,
      setRunOnApply,
      trackPanelSettingsChange,
      updateConfig,
    ]
  );

  const smoothingDisplayValues = useMemo(
    () => ({
      smoothingWeight,
      smoothingType,
    }),
    [smoothingType, smoothingWeight]
  );

  const smoothingValues = useMemo(
    () => ({
      smoothingWeight: config.smoothingWeight,
      smoothingType: config.smoothingType,
    }),
    [config.smoothingType, config.smoothingWeight]
  );

  const handleTrackSmoothingSetting = useCallback(
    (setting: SettingType, value: string | undefined) => {
      trackPanelSettingsChange({
        setting,
        settingValue: value,
      });
    },
    [trackPanelSettingsChange]
  );

  const handleClearSmoothing = useCallback(() => {
    updateConfig(EMPTY_SMOOTHING_SETTINGS);
  }, [updateConfig]);

  const xAxisTitle = (
    <DebouncedInputOption
      value={config.xAxisTitle}
      update={handleXAxisTitleUpdate}
      placeholder={prettyXAxisLabel(xAxis, lines)}
    />
  );
  const yAxisTitle = (
    <DebouncedInputOption
      value={config.yAxisTitle}
      update={handleYAxisTitleUpdate}
    />
  );

  const xAxisRange = (
    <div>
      {!xAxisIsBuiltin(xAxis) ? (
        <Tailwind>
          <SettingIndentedRow className="ml-0">
            <div className="ml-12">
              <XAxisFormat
                trackSetting={NOOP_TRACK_SETTING}
                updateSetting={updateConfig}
                xAxisFormat={xAxisFormat}
              />
              <div className="flex min-w-[360px] items-center">
                <SettingLabel text="Range" className="ml-14 w-[87px]" />
                <RangeInput
                  disabled={
                    isEmptyChart(config) ||
                    (renderingBarPlot && xAxisMin == null && xAxisMax == null)
                  }
                  label="X Axis"
                  onMinChange={newVal => {
                    if (
                      (configZoom.xAxisMax ?? Infinity) < (newVal ?? -Infinity)
                    ) {
                      return;
                    }
                    const value = newVal ?? null;
                    if (isBucketing) {
                      updateConfig({
                        xAxisMin: value,
                      });
                    } else {
                      handleConfigZoomChange({
                        xAxisMin: value,
                      });
                    }
                    trackConfigZoomUsage('x', value, configZoom.xAxisMax);
                  }}
                  onMaxChange={newVal => {
                    if (
                      (configZoom.xAxisMin ?? -Infinity) > (newVal ?? Infinity)
                    ) {
                      return;
                    }
                    const value = newVal ?? null;
                    if (isBucketing) {
                      updateConfig({
                        xAxisMax: value,
                      });
                    } else {
                      handleConfigZoomChange({
                        xAxisMax: value,
                      });
                    }
                    trackConfigZoomUsage('x', configZoom.xAxisMin, value);
                  }}
                  minValue={configZoom.xAxisMin}
                  maxValue={configZoom.xAxisMax}
                  log={true}
                  logValue={xLogScale}
                  onLogChange={newVal => {
                    updateConfig({
                      xLogScale: newVal,
                    });
                  }}
                />
              </div>
            </div>
          </SettingIndentedRow>
        </Tailwind>
      ) : (
        <RangeInput
          disabled={
            isEmptyChart(config) ||
            (renderingBarPlot && xAxisMin == null && xAxisMax == null)
          }
          label="X Axis"
          onMinChange={newVal => {
            if ((configZoom.xAxisMax ?? Infinity) < (newVal ?? -Infinity)) {
              return;
            }
            const value = newVal ?? null;
            if (isBucketing) {
              updateConfig({
                xAxisMin: value,
              });
            } else {
              handleConfigZoomChange({
                xAxisMin: value,
              });
            }
            trackConfigZoomUsage('x', value, configZoom.xAxisMax);
          }}
          onMaxChange={newVal => {
            if ((configZoom.xAxisMin ?? -Infinity) > (newVal ?? Infinity)) {
              return;
            }
            const value = newVal ?? null;
            if (isBucketing) {
              updateConfig({
                xAxisMax: value,
              });
            } else {
              handleConfigZoomChange({
                xAxisMax: value,
              });
            }
            trackConfigZoomUsage('x', configZoom.xAxisMin, value);
          }}
          minValue={configZoom.xAxisMin}
          maxValue={configZoom.xAxisMax}
          log={true}
          logValue={xLogScale}
          onLogChange={newVal => {
            updateConfig({
              xLogScale: newVal,
            });
          }}
        />
      )}
    </div>
  );

  const yAxisRange = (
    <RangeInput
      disabled={isEmptyChart(config)}
      onMinChange={newVal => {
        if ((configZoom.yAxisMax ?? Infinity) < (newVal ?? -Infinity)) {
          return;
        }
        const value = newVal ?? null;
        if (isBucketing) {
          updateConfig({yAxisMin: value});
        } else {
          handleConfigZoomChange({yAxisMin: value});
        }
        trackConfigZoomUsage('y', value, configZoom.yAxisMax);
      }}
      onMaxChange={newVal => {
        if ((configZoom.yAxisMin ?? -Infinity) > (newVal ?? Infinity)) {
          return;
        }
        const value = newVal ?? null;
        if (isBucketing) {
          updateConfig({yAxisMax: value});
        } else {
          handleConfigZoomChange({yAxisMax: value});
        }
        trackConfigZoomUsage('y', configZoom.yAxisMin, value);
      }}
      minValue={configZoom.yAxisMin}
      maxValue={configZoom.yAxisMax}
      label="Y Axis"
      log
      logValue={yLogScale}
      onLogChange={newVal =>
        updateConfig({
          yLogScale: newVal,
        })
      }
    />
  );

  return (
    <Tab.Pane as="div" className="form-grid" data-test="edit-data-tab">
      <Tailwind>
        <div className="mt-8 flex items-center pb-16">
          <SettingLabel text="Chart title" />
          <DebouncedInputOption
            value={config.chartTitle}
            update={handleChartTitleUpdate}
            placeholder={defaultTitle(config)}
          />
        </div>
      </Tailwind>

      {useMetricSearchPicker ? (
        <MetricSearchAxisSections
          xAxisRange={xAxisRange}
          xAxisTitle={xAxisTitle}
          yAxisRange={yAxisRange}
          yAxisTitle={yAxisTitle}
          onResetXAxis={handleResetXAxis}
          runSetRefs={workspaceRefs.runSetRefs}
          customRunNamesRef={workspaceRefs.customRunNamesRef}
          config={metricsPickerConfig}
          xAxis={xAxis}
          onXAxisChange={handleXAxisChange}
          updateConfig={updateConfig}
        />
      ) : (
        <HistoryKeysAxisSections
          xAxisRange={xAxisRange}
          xAxisTitle={xAxisTitle}
          yAxisRange={yAxisRange}
          yAxisTitle={yAxisTitle}
          onResetXAxis={handleResetXAxis}
          config={config}
          xAxis={xAxis}
          isSingleRun={isSingleRun}
          entityName={query.entityName}
          projectName={query.projectName}
          onXAxisChange={handleXAxisChange}
          updateConfig={updateConfig}
        />
      )}

      {!isSingleRun && (
        <EditMaxRuns
          config={config}
          isGrouped={isGrouped}
          // oxlint-disable-next-line react/refs
          settingsToTrack={settingsToTrack.current}
          setRunOnApply={setRunOnApply}
          trackPanelSettingsChange={trackPanelSettingsChange}
          updateConfig={updateConfig}
        />
      )}

      {isPointVizFullFidelity(pointVisualizationMethod) && (
        <TailwindContents>
          <LinePlotConfigExcludeOutliers
            excludeOutliers={excludeOutliers ?? 'include-outliers'}
            onExcludeOutliersChange={handleExcludeOutliersChange}
            disabled={
              effectiveFullFidelityMode === FULL_FIDELITY_MODES.MinMaxLast
            }
          />
        </TailwindContents>
      )}

      <TailwindContents>
        <LinePlotPointAggregation
          className={twMerge('group mt-12')}
          clearSetting={handleClearPointVisualization}
          displayValues={pointVisualizationDisplayValues}
          onPointVisualizationMethodChange={
            handlePointVisualizationMethodChange
          }
          onFullFidelityModeChange={handleFullFidelityModeChange}
          settingLevel="panel"
          values={pointVisualizationValues}
        />
      </TailwindContents>

      <EditRunsTableGrouping
        config={config}
        setRunOnApply={setRunOnApply}
        trackPanelSettingsChange={trackPanelSettingsChange}
        updateConfig={updateConfig}
      />

      <TailwindContents>
        <LinePlotSmoothing
          className={twMerge(
            'group my-12',
            smoothingType === SmoothingType.None ? 'mb-8' : ''
          )}
          displayValues={smoothingDisplayValues}
          settingLevel="panel"
          updateSetting={updateConfig}
          trackSetting={handleTrackSmoothingSetting}
          clearSetting={handleClearSmoothing}
          values={smoothingValues}
        />
      </TailwindContents>
      {smoothingWeight !== 0 && (isBucketing || !isGrouped) && (
        <Tailwind>
          <div className="mt-8 flex items-center">
            <Tooltip
              trigger={<SettingLabel text="Show Original" />}
              content="Show lines before smoothing"
            />
            <Switch.Root
              name="aggregate"
              size="medium"
              checked={showOriginalAfterSmoothing}
              onCheckedChange={checked => {
                updateConfig({
                  showOriginalAfterSmoothing: checked,
                });
                trackPanelSettingsChange({
                  setting: 'showOriginalAfterSmoothing',
                  settingValue: checked.toString(),
                });
              }}>
              <Switch.Thumb
                size="medium"
                checked={showOriginalAfterSmoothing}
              />
            </Switch.Root>
          </div>
        </Tailwind>
      )}
      {!renderingBarPlot && (
        <div data-test="plot-type-selection">
          <PlotTypeSelection
            config={config as RunsLinePlotConfig}
            updateConfig={updateConfig}
          />
        </div>
      )}
    </Tab.Pane>
  );
};
